import { useRef, useEffect, useCallback } from 'react';

interface UseClickDragDetectionOptions {
    onDragStart: (e: React.MouseEvent) => void;
    onEditClick: () => void;
    dragThreshold?: number;
}

export const useClickDragDetection = ({
    onDragStart,
    onEditClick,
    dragThreshold = 5
}: UseClickDragDetectionOptions) => {
    const dragStartRef = useRef<{ x: number; y: number; started: boolean } | null>(null);
    const eventHandlersRef = useRef<{
        mousemove?: (e: MouseEvent) => void;
        mouseup?: (e: MouseEvent) => void;
    }>({});

    // Cleanup event listeners on unmount
    useEffect(() => {
        return () => {
            if (eventHandlersRef.current.mousemove) {
                document.removeEventListener('mousemove', eventHandlersRef.current.mousemove);
            }
            if (eventHandlersRef.current.mouseup) {
                document.removeEventListener('mouseup', eventHandlersRef.current.mouseup);
            }
        };
    }, []);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        // Only left mouse button
        if (e.button !== 0) return;
        e.stopPropagation();

        // Store initial mouse position
        dragStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            started: false
        };

        const handleMouseMove = (moveEvent: MouseEvent) => {
            if (!dragStartRef.current) return;

            const deltaX = Math.abs(moveEvent.clientX - dragStartRef.current.x);
            const deltaY = Math.abs(moveEvent.clientY - dragStartRef.current.y);
            const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

            // Start drag if mouse moved more than threshold
            if (distance > dragThreshold && !dragStartRef.current.started) {
                dragStartRef.current.started = true;
                onDragStart(e);
                if (eventHandlersRef.current.mousemove) {
                    document.removeEventListener('mousemove', eventHandlersRef.current.mousemove);
                }
                if (eventHandlersRef.current.mouseup) {
                    document.removeEventListener('mouseup', eventHandlersRef.current.mouseup);
                }
            }
        };

        const handleMouseUp = () => {
            if (eventHandlersRef.current.mousemove) {
                document.removeEventListener('mousemove', eventHandlersRef.current.mousemove);
            }
            if (eventHandlersRef.current.mouseup) {
                document.removeEventListener('mouseup', eventHandlersRef.current.mouseup);
            }
            // Reset drag state after a short delay to allow click to fire
            setTimeout(() => {
                if (dragStartRef.current) {
                    dragStartRef.current = null;
                }
            }, 10);
        };

        eventHandlersRef.current.mousemove = handleMouseMove;
        eventHandlersRef.current.mouseup = handleMouseUp;
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [onDragStart, dragThreshold]);

    const handleClick = useCallback(() => {
        // Only allow edit if drag wasn't started
        if (!dragStartRef.current?.started) {
            onEditClick();
        }
    }, [onEditClick]);

    return {
        handleMouseDown,
        handleClick,
        isDragStarted: dragStartRef.current?.started || false
    };
}; 